import { useState, useRef, useEffect } from "react";
import { HelpCircle, X, ExternalLink } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@core/button";
import { Text } from "@core/text";
import { useTranslation } from "react-i18next";
import ReactPlayer from "react-player";

interface ToolHelperProps {
  toolPath: string;
  learnMoreLink?: string;
  shouldShowTour?: boolean;
  toolType?: string;
}

const toolVideos = {
  podcast: "4podcast.mp4",
  rubric: "23rubriküretici.mp4",
  vocabularies: "6kelimeler%20rehberi.mp4",
  "interactive-materials": "5etkileşimlimateryal.mp4",
  "text-leveler": "metin_seviye_ayarlayici.mp4",
  collaborative: "16isbirlikciogrenme.mp4",
  "choice-boards": "12secimpanoları.mp4",
  "exit-tickets": "22cıkısbileti.mp4",
  hooks: "11derskancaları.mp4",
  connections: "10gercekdunyabaglantıları.mp4",
  "essential-questions": "18temelsorular.mp4",
  "formative-assessment": "21bicimlendiricidegerlendirme.mp4",
  "ai-supported-tasks": "19yzdestekligörevler.mp4",
  "blooms-taxonomy-activities": "20bloomtaxo.mp4",
  "classroom-management": "13sınıfyönetimi.mp4",
  "performance-tasks": "25performansgörevleri.mp4",
  "project-based-learning": "15projetabanlıogrenme.mp4",
  "homework-ideas": "24ödevler.mp4",
  images: "hayalatolyesi2.mp4",
  summaries: "7metinözetleyici.mp4",
  misconceptions: "17kavramyanılgıları.mp4",
  "ai-assignment-designer": "yzdegerlendirme.mp4",
  "ai-assessment-scale": "aiassessmentscale.mp4",
  "transfer-activity": "transfergorevleri.mp4",
  "ai-resistant-homework-ideas": "24ödevler.mp4",
  "video-avatar-generator": "",
};

const toolThumbnails = {
  podcast: "/tutorial_thumbnails/podcast.png",
  rubric: "/tutorial_thumbnails/rubric.png",
  vocabularies: "/tutorial_thumbnails/vocabularies.png",
  "ai-assignment-designer": "/tutorial_thumbnails/ai-assignment-designer.png",
  "interactive-materials": "/tutorial_thumbnails/interactive-materials.png",
  "text-leveler": "/tutorial_thumbnails/text-leveler.png",
  summaries: "/tutorial_thumbnails/summaries.png",
  collaborative: "/tutorial_thumbnails/collaborative.png",
  "choice-boards": "/tutorial_thumbnails/choice-boards.png",
  misconceptions: "/tutorial_thumbnails/misconceptions.png",
  "exit-tickets": "/tutorial_thumbnails/exit-tickets.png",
  hooks: "/tutorial_thumbnails/hooks.png",
  connections: "/tutorial_thumbnails/connections.png",
  "essential-questions": "/tutorial_thumbnails/essential-questions.png",
  "formative-assessment": "/tutorial_thumbnails/formative-assessment.png",
  "ai-supported-tasks": "/tutorial_thumbnails/ai-supported-tasks.png",
  "blooms-taxonomy-activities":
    "/tutorial_thumbnails/blooms-taxonomy-activities.png",
  "classroom-management": "/tutorial_thumbnails/classroom-management.png",
  "performance-tasks": "/tutorial_thumbnails/performance-tasks.png",
  "project-based-learning": "/tutorial_thumbnails/project-based-learning.png",
  "transfer-activity": "/tutorial_thumbnails/transfer-activity.png",
  "ai-resistant-homework-ideas":
    "/tutorial_thumbnails/ai-resistant-homework-ideas.png",
  "homework-ideas": "/tutorial_thumbnails/homework-ideas.png",
  "ai-assessment-scale": "/tutorial_thumbnails/ai-assessment-scale.png",
  images: "/tutorial_thumbnails/images.png",
  "video-avatar-generator": "/tutorial_thumbnails/video-avatar-generator.png",
};

const toolUrls = {
  podcast:
    "https://akademi.madlen.io/c/madlen102/sections/559280/lessons/2103850",
  rubric:
    "https://akademi.madlen.io/c/madlen-iv/sections/559655/lessons/2105021",
  vocabularies:
    "https://akademi.madlen.io/c/madlen102/sections/559374/lessons/2104126",
  "ai-assignment-designer":
    "https://akademi.madlen.io/c/madlen-v/sections/597790/lessons/2258730",
  "interactive-materials":
    "https://akademi.madlen.io/c/madlen102/sections/559357/lessons/2104025",
  "text-leveler":
    "https://akademi.madlen.io/c/madlen-ii/sections/559560/lessons/2104709",
  summaries:
    "https://akademi.madlen.io/c/madlen-ii/sections/559388/lessons/2104172",
  collaborative:
    "https://akademi.madlen.io/c/madlen-iii/sections/559587/lessons/2104791",
  "choice-boards":
    "https://akademi.madlen.io/c/madlen-ii/sections/559574/lessons/2104753",
  misconceptions:
    "https://akademi.madlen.io/c/madlen-iii/sections/559588/lessons/2104799",
  "exit-tickets":
    "https://akademi.madlen.io/c/madlen-iv/sections/559654/lessons/2105014",
  hooks:
    "https://akademi.madlen.io/c/madlen-ii/sections/559570/lessons/2104743",
  connections:
    "https://akademi.madlen.io/c/madlen-ii/sections/559569/lessons/2104741",
  "essential-questions":
    "https://akademi.madlen.io/c/madlen-iii/sections/559589/lessons/2104802",
  "formative-assessment":
    "https://akademi.madlen.io/c/madlen-iv/sections/559624/lessons/2104959",
  "ai-supported-tasks":
    "https://akademi.madlen.io/c/madlen-iii/sections/559590/lessons/2104804",
  "blooms-taxonomy-activities":
    "https://akademi.madlen.io/c/madlen-iv/sections/559622/lessons/2104961",
  "classroom-management":
    "https://akademi.madlen.io/c/madlen-iii/sections/559585/lessons/2104784",
  "performance-tasks":
    "https://akademi.madlen.io/c/madlen-iv/sections/559697/lessons/2105229",
  "project-based-learning":
    "https://akademi.madlen.io/c/madlen-iii/sections/559586/lessons/2104789",
  "transfer-activity":
    "https://akademi.madlen.io/c/madlen-v/sections/597773/lessons/2258704",
  "ai-resistant-homework-ideas":
    "https://akademi.madlen.io/c/madlen-iv/sections/559669/lessons/2105063",
  "homework-ideas":
    "https://akademi.madlen.io/c/madlen-iv/sections/559669/lessons/2105063",
  "ai-assessment-scale":
    "https://akademi.madlen.io/c/madlen-v/sections/598527/lessons/2261247",
  images:
    "https://akademi.madlen.io/c/madlen-ii/sections/559561/lessons/2104711",
  "video-avatar-generator": "",
};

const ToolHelper = ({
  toolPath,
  learnMoreLink,
  shouldShowTour = false,
  toolType = "",
}: ToolHelperProps) => {
  console.log(toolPath);
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(shouldShowTour);
  const modalRef = useRef<HTMLDivElement>(null);

  // Reset tooltip visibility when shouldShowTour changes
  useEffect(() => {
    setShowTooltip(shouldShowTour);
  }, [shouldShowTour]);

  // Handle click outside to close modal
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleButtonClick = () => {
    setIsOpen(true);
    setShowTooltip(false);
  };

  return (
    <>
      {/* Floating Help Button with Chat Bubble */}
      <motion.div
        className="fixed bottom-6 right-6 z-50"
        whileTap={{ scale: 0.95 }}
      >
        {/* Chat Bubble Tooltip */}
        <AnimatePresence>
          {showTooltip && (
            <motion.div
              className="absolute bottom-full right-0 mb-2 p-3 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-xl shadow-lg min-w-[320px] max-w-xs cursor-pointer"
              initial={{ opacity: 0, y: 10, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 1 }}
              transition={{ duration: 0.2 }}
              style={{ transformOrigin: "bottom right" }}
              onClick={handleButtonClick}
            >
              <Button
                onClick={(e) => {
                  console.log("clicked");
                  e.stopPropagation();
                  setShowTooltip(false);
                }}
                variant="solid"
                size="icon"
                rounded="full"
                className="absolute top-1 right-1 p-2"
                bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X size={14} className="text-gray-500" />
              </Button>
              <Text variant="p" size="lg" className="pr-6">
                {t("common.howToUseDescription", {
                  toolType: toolType,
                })}
              </Text>
              <div className="absolute bottom-0 right-4 transform translate-y-1/2 rotate-45 w-4 h-4 bg-white dark:bg-gray-800"></div>
            </motion.div>
          )}
        </AnimatePresence>

        <Button
          onClick={handleButtonClick}
          variant="solid"
          size="icon"
          rounded="full"
          bgColor="bg-primary-500 hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-700"
          textColor="text-white"
          className="w-12 h-12 shadow-lg"
        >
          <HelpCircle className="w-6 h-6" />
        </Button>
      </motion.div>

      {/* Tutorial Modal */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-[9999] overflow-hidden bg-black bg-opacity-50 flex items-center justify-center p-4">
            <motion.div
              ref={modalRef}
              className="bg-white dark:bg-gray-800 rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: "spring", damping: 15 }}
            >
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <Text variant="h2">
                  {t("common.howToUse", { toolType: toolType })}
                </Text>
                <Button
                  onClick={() => setIsOpen(false)}
                  variant="solid"
                  size="icon"
                  rounded="full"
                  bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <X size={24} className="text-gray-500" />
                </Button>
              </div>

              <div className="flex-1 overflow-auto p-6">
                <div className="flex flex-col items-center gap-6">
                  {/* Video Player */}
                  <div className="w-full aspect-video rounded-xl">
                    {toolVideos[toolPath as keyof typeof toolVideos] && (
                      <ReactPlayer
                        url={
                          "https://d3w0zaqalp9ahr.cloudfront.net/" +
                          toolVideos[toolPath as keyof typeof toolVideos]
                        }
                        controls
                        playing={true}
                        light={
                          toolThumbnails[
                            toolPath as keyof typeof toolThumbnails
                          ]
                        }
                        onError={(e) => {
                          console.log(e);
                        }}
                      />
                    )}
                  </div>

                  <Button
                    onClick={() =>
                      window.open(
                        toolUrls[toolPath as keyof typeof toolUrls],
                        "_blank",
                      )
                    }
                    variant="solid"
                    size="md"
                    rounded="lg"
                    bgColor="bg-primary-500 hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-700"
                    textColor="text-white"
                    rightIcon={<ExternalLink size={16} />}
                  >
                    {t("common.learnMore")}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ToolHelper;
